import { z } from 'zod';

/**
 * Client schema for the campaign outcome-tracking surface (SCRUM-72/73/74), mirroring the
 * server DTOs `CampaignDetail` / `CampaignOutcomeRow`
 * (Core.Application/Campaigns/ICampaignQueryService.cs). Minimal-API serializes with
 * camelCase, so the field names below match the wire shape exactly. Validated on fetch per
 * the established api-client convention (see referrals-api.ts).
 */

/**
 * The five campaign outcome statuses (server enum `CampaignOutcomeStatus`, persisted as its
 * PascalCase name). `Pending` is the seeded default; `Acquired` is the terminal positive
 * outcome (honored in the summary counts, not by UI transition-locking — SCRUM-74 D4).
 */
export const CAMPAIGN_OUTCOME_STATUSES = [
  'Pending',
  'Interested',
  'Acquired',
  'NotInterested',
  'NoResponse',
] as const;

export const campaignOutcomeStatusSchema = z.enum(CAMPAIGN_OUTCOME_STATUSES);
export type CampaignOutcomeStatus = z.infer<typeof campaignOutcomeStatusSchema>;

/** One row of the outcome table (SCRUM-73). */
export const campaignOutcomeRowSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  productName: z.string().nullable(),
  matchMet: z.number(),
  matchTotal: z.number(),
  status: campaignOutcomeStatusSchema,
  /** Non-null only when the customer entered via a teller referral (amber attribution). */
  referredByName: z.string().nullable(),
  updatedAt: z.string(),
});
export type CampaignOutcomeRow = z.infer<typeof campaignOutcomeRowSchema>;

/** A campaign with its per-customer outcome rows (GET /api/campaigns/{id}). */
export const campaignDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  status: z.string(),
  outcomes: campaignOutcomeRowSchema.array(),
});
export type CampaignDetail = z.infer<typeof campaignDetailSchema>;

/**
 * A campaign row for the persistent selector (SCRUM-71), mirroring the server DTO
 * `CampaignSummary` (GET /api/campaigns list). Replaces the session-scoped recent-campaign
 * chips with a server-backed list so campaigns remain reachable across sessions.
 */
export const campaignSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  status: z.string(),
  targetCount: z.number().int(),
});
export type CampaignSummary = z.infer<typeof campaignSummarySchema>;
